import { useState } from "react";
import { api, ApiError } from "../api";
import { FolderField } from "./Incoming";
import { Rubric } from "./Rubric";
import { RemoveFromPlan } from "./PlanEdit";
import { folderLabel, namesTaken } from "../home";
import type { CourseSection, HomeView, PlanLesson, PlanUnit } from "../home";
import { SCHEMA_VERSION, readLesson, readUnit } from "../content/model";
import { resolveView } from "../view/produce";
import { renderSpec } from "../view/render";
import { SpecDebug, specDebugAsked } from "../view/SpecDebug";

// A unit or a lesson, read properly.
//
// This was rendered inline in the sidebar, where a teaching task wrapped to six lines in a 250px
// column and the texts sat behind a disclosure triangle. That is a tooltip that will not close,
// not a page — and the plan is the thing the whole product is organised around.
//
// The nav still reveals the one line that helps you navigate. Everything else is here, at a width
// where it can be read.
//
// THIS FILE NO LONGER DRAWS A UNIT OR A LESSON. It asks `resolveView` for a view of one and
// renders what comes back — a spec, from the schema-derived default or from an authored file, and
// one day from a model. What is left here is everything a spec may NOT describe: the folders of
// student work, the rubric, and the controls that change the plan. Those are passed in as host
// slots, by name, because a spec binds to curriculum content only and a producer that could reach
// a student's scores would be one that has to be trusted with them.
//
// See docs/SPEC-RENDERING.md.
//
// Nothing is fetched: the plan is already in the client, because the sidebar is drawn from it. A
// request to re-read what is on screen would be a second answer that can disagree with the first.

const STATE_WORD: Record<PlanLesson["state"], string> = {
  not_taught: "not taught yet",
  taught: "taught",
  collecting: "work coming in",
  scored: "scored",
  released: "handed back",
};

/** Make a FOLDER OF STUDENT WORK for this lesson: which work it is, and, if they have it, the
 *  Google Drive folder it comes from.
 *
 *  TWO DIFFERENT FOLDERS, one form. The folder of student work is the thing being made — this
 *  lesson's drafts, for this cohort. The Drive folder is where that work comes from, which today is
 *  how it gets in at all, and is still a separate fact about it: it can be added later, or changed
 *  afterwards on the row itself (see `WorkFolder` in ../home). Asking for both in one step is the
 *  right convenience; each field saying which folder it means is what stops them reading as one.
 *
 *  Until this existed the only folders of student work were the two a seed script had written, so
 *  every other lesson in the plan had nowhere to put its work, and the Drive editor beside the
 *  check button could only change a folder that already existed.
 */
function AddCollection({ sections, lesson, onOpen, onCreated }: {
  /** The teacher's sections of this course. A folder of student work belongs to ONE of them, so
   *  with more than one there is a question to ask; with one there is not. */
  sections: CourseSection[];
  lesson: PlanLesson;
  onOpen: (href: string) => void;
  onCreated: () => void;
}) {
  const [sectionId, setSectionId] = useState(sections[0]?.section_id ?? "");
  const taken = namesTaken(lesson.work, sectionId);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ label: string; href: string } | null>(null);

  const clash = taken.has(label.trim().toLowerCase());

  const create = () => {
    setSaving(true); setError(null);
    api.post<{ iteration: string; href: string }>(
      "/collections",
      { section_id: sectionId, lesson_id: lesson.lesson_id, iteration: label, url, name })
      .then((r) => {
        // The confirmation stays on screen. Adding a folder and watching the fields simply empty
        // themselves is how the last version of this read as nothing having happened.
        setMade({ label: r.iteration, href: r.href });
        setOpen(false); setLabel(""); setUrl(""); setName("");
        onCreated();
      })
      // The server's sentence, which says what is wrong with the link or why the lesson was
      // refused. A generic "could not be saved" throws away the only useful part.
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "could not be added"))
      .finally(() => setSaving(false));
  };

  if (!open) {
    return (
      <div className="pv-add">
        <button className="btn" onClick={() => { setOpen(true); setMade(null); }}>
          {lesson.work.length === 0 ? "Add a folder of student work" : "Add another folder"}
        </button>
        {made && (
          <span className="pv-added">
            {folderLabel(made.label)} added.{" "}
            {/* Where it went. The new folder is in the list above this button, and a teacher who
                has just made one usually wants to be in it. */}
            <button className="btn-link" onClick={() => onOpen(made.href)}>Open it</button>
          </span>
        )}
        {error && <span className="inc-error">{error}</span>}
      </div>
    );
  }

  return (
    <div className="pv-add pv-add-form">
      {/* WHOSE work. One sequence, taught to several cohorts: the folder belongs to one of them,
          and "Final" can be taken in period 4 and free in period 6 because the section is part of
          the key. A course with one section asks nothing. */}
      {sections.length > 1 && (
        <div className="pv-which">
          {sections.map((sec) => (
            <button key={sec.section_id}
                    className={`btn${sec.section_id === sectionId ? " primary" : ""}`}
                    disabled={saving} onClick={() => setSectionId(sec.section_id)}>
              {sec.label}
            </button>
          ))}
        </div>
      )}
      <input value={label} autoFocus disabled={saving}
             placeholder="What do you call this work?"
             onChange={(e) => setLabel(e.target.value)} />
      <input value={url} size={34} disabled={saving}
             placeholder="Paste the Google Drive folder link — or add it later"
             onChange={(e) => setUrl(e.target.value)} />
      <button className="btn primary" disabled={saving || !label.trim() || clash}
              onClick={create}>
        {saving ? "Adding…" : "Add it"}
      </button>
      <button className="btn" disabled={saving}
              onClick={() => { setOpen(false); setError(null); setLabel(""); setUrl(""); setName(""); }}>
        Cancel
      </button>

      {/* A lesson with no task has no rubric behind it. Work can be collected — that is an
          ordinary state the plan already shows as "taught and not scored" — and saying so here is
          what stops a teacher waiting for scores that were never coming. */}
      {!lesson.scored && (
        <p className="pv-quiet pv-add-note">
          This lesson has no rubric attached, so work collected here will not be scored.
        </p>
      )}
      {clash && (
        <p className="pv-quiet pv-add-note">
          This lesson already has a folder called “{label.trim()}”.
        </p>
      )}
      {error && <span className="inc-error">{error}</span>}
    </div>
  );
}

function Collections({ sections, lesson, onOpen, onChanged }: {
  sections: CourseSection[];
  lesson: PlanLesson;
  onOpen: (href: string) => void;
  onChanged: () => void;
}) {
  // Only where there is more than one. On a single-section course the same label under every
  // folder is noise.
  const showSection = sections.length > 1;
  return (
    <section className="pv-section">
      <h3>Student work</h3>
      {lesson.work.length === 0 ? (
        <p className="pv-quiet">
          No student work has been collected for this lesson yet.
        </p>
      ) : (
        <ul className="pv-collections">
          {lesson.work.map((w) => (
            // The row opens the collection; the folder cell EDITS it. Two jobs, so two targets —
            // and a button cannot live inside a button, which is why the border moved to the row.
            <li key={w.activity_id} className="pv-c-row">
              <button className="pv-collection" onClick={() => onOpen(w.href)}>
                <span className="pv-c-name">
                  {folderLabel(w.iteration) || "Student work"}
                  {showSection && <span className="pv-c-for">{w.section_label}</span>}
                </span>
                <span className="pv-c-state">
                  {w.handed_in
                    ? `${w.handed_in.have} of ${w.handed_in.expected} in`
                    : STATE_WORD[w.state]}
                </span>
              </button>
              {/* The folder is the COLLECTION's — a draft and a final can be two folders, or the
                  same one named twice — and "no folder yet" was a state a teacher could read here
                  and only fix by opening the collection to find the one editor that existed. It
                  is the same editor, in the place the gap is visible. */}
              <FolderField activityId={w.activity_id} url={w.folder_url ?? null}
                           name={w.folder_name ?? null} emptyLabel="Add the Google Drive folder"
                           onSaved={onChanged} />
            </li>
          ))}
        </ul>
      )}
      <AddCollection sections={sections} lesson={lesson} onOpen={onOpen}
                     onCreated={onChanged} />
    </section>
  );
}

/** The lessons under a unit, as a list you can click. Host-rendered: a lesson's STATE is student
 *  work — taught, collecting, scored — and a spec may not bind to that. Where the list goes is the
 *  spec's business, which is what the `lessons` slot says. */
function LessonList({ unit, onOpen }: { unit: PlanUnit; onOpen: (href: string) => void }) {
  return (
    <section className="pv-section">
      <h3>Lessons</h3>
      <ol className="pv-lessons">
        {unit.lessons.map((l) => (
          <li key={l.lesson_id}>
            <button className="pv-lesson"
                    onClick={() => onOpen(`/writing/l/${encodeURIComponent(l.lesson_id)}`)}>
              <span className="pv-l-ord">{l.ordinal ?? ""}</span>
              <span className="pv-l-title">{l.title}</span>
              <span className="pv-l-state">{STATE_WORD[l.state]}</span>
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** The unit or lesson a plan URL names, found in the plan already on screen. */
function find(plan: HomeView | null, kind: "unit" | "lesson", id: string) {
  for (const course of plan?.courses ?? []) {
    for (const u of course.units) {
      if (kind === "unit" && u.unit_id === id) return { course, unit: u, lesson: null };
      for (const l of u.lessons) {
        if (kind === "lesson" && l.lesson_id === id) return { course, unit: u, lesson: l };
      }
    }
  }
  return null;
}

export function PlanView({ plan, kind, id, onOpen, onCreated }: {
  plan: HomeView | null;
  kind: "unit" | "lesson";
  id: string;
  onOpen: (href: string) => void;
  /** A collection was made: the plan on screen is now one collection out of date. The answer
   *  comes from the server rather than being patched in here — a client that invented the row
   *  would show a folder the plan does not have if anything about the write was different. */
  onCreated: () => void;
}) {
  if (!plan) return <div className="pv"><p className="pv-quiet">Reading your plan…</p></div>;
  const found = find(plan, kind, id);
  if (!found) {
    // To this teacher it does not exist. The database hides another class's plan, so a link to
    // one is a 404 rather than a quiet switch to their own.
    return (
      <div className="pv">
        <h2>Not found</h2>
        <p className="pv-quiet">
          There is no {kind} here by that name. It may belong to a cohort you do not teach.
        </p>
      </div>
    );
  }

  const { course, unit, lesson } = found;
  const node: PlanUnit | PlanLesson = lesson ?? unit;
  const about = node.about ?? {};

  // THE CONTENT, then a view of it. `readUnit`/`readLesson` turn what the API sent into the shape
  // the content schema describes; `resolveView` answers with a spec — authored if somebody wrote
  // one for this context, otherwise derived from the schema. Neither step knows what a unit is
  // for; both would work on a kind of thing invented next week.
  const content = lesson
    ? readLesson(lesson, { courseName: course.course_name,
                           unitTitle: unit.title ?? undefined })
    : readUnit(unit, { courseName: course.course_name });
  const view = resolveView({ entityType: kind, audience: "teacher", intent: "detail",
                             schemaVersion: SCHEMA_VERSION });


  // WHAT A SPEC MAY NOT DESCRIBE. Folders of student work, the rubric, and the controls that
  // change the plan: all of them read or write things that are not curriculum. The spec says
  // where they go by name; what goes in them is decided here.
  const slots = lesson
    ? {
        work: <Collections sections={course.sections} lesson={lesson} onOpen={onOpen}
                           onChanged={onCreated} />,
        rubric: lesson.task_id
          ? <Rubric taskId={lesson.task_id} resourceId={lesson.lesson_id} />
          : null,
        actions: (
          <RemoveFromPlan kind="lesson" resourceId={lesson.lesson_id}
                          onRemoved={() => { onCreated();
                                             onOpen(`/writing/u/${unit.unit_id ?? ""}`); }} />
        ),
      }
    : {
        lessons: <LessonList unit={unit} onOpen={onOpen} />,
        // ADDING IS IN THE NAV, on the row it goes into. Only removal is here.
        actions: unit.unit_id ? (
          <RemoveFromPlan kind="unit" resourceId={unit.unit_id}
                          onRemoved={() => { onCreated(); onOpen("/writing"); }} />
        ) : null,
      };

  const viewCtx = { entityType: kind, audience: "teacher" as const, intent: "detail" as const,
                    schemaVersion: SCHEMA_VERSION };

  return (
    <>
      {renderSpec(view.spec, content, slots)}
      {/* `?spec=debug`: what drew this, and the same content drawn by the default beside it. */}
      {specDebugAsked() && <SpecDebug ctx={viewCtx} content={content} slots={slots} />}
      {Object.keys(about).length === 0 && (
        <p className="pv-quiet pv-undescribed">
          Nobody has described this {kind} yet. When an LDC module is pulled in, its question,
          task and texts land here.
        </p>
      )}
    </>
  );
}
